/**
 * Common Code Parser Utils
 * 공통코드 API 응답 파싱 유틸리티
 */

import { CommonCodeItem, CommonCodeGroup, CommonCodeApiResponse } from '../../commonCode/types/systemCommonCode.types';

/**
 * API 응답에서 codes 배열 추출
 * 다양한 API 응답 구조를 지원 (data.codes, codes, 배열 직접)
 * @param response API 응답 객체
 */
export const extractCodesFromResponse = (response: CommonCodeApiResponse): CommonCodeItem[] => {
    if (!response || typeof response !== 'object') {
        return [];
    }

    // data.codes 구조
    if ('data' in response && response.data && typeof response.data === 'object') {
        if ('codes' in response.data && Array.isArray(response.data.codes)) {
            return response.data.codes;
        }
    }

    // 직접 codes 배열인 경우
    if ('codes' in response && Array.isArray(response.codes)) {
        return response.codes;
    }

    // 배열 자체인 경우 - CommonCodeItem인지 확인
    if (Array.isArray(response)) {
        if (response.length > 0 && 'comCode' in (response[0] as object)) {
            return response as CommonCodeItem[];
        }
    }

    return [];
};

/**
 * API 응답에서 groups 배열 추출
 * 다양한 API 응답 구조를 지원 (data.groups, groups)
 * @param response API 응답 객체
 */
export const extractGroupsFromResponse = (response: CommonCodeApiResponse): CommonCodeGroup[] => {
    if (!response || typeof response !== 'object') {
        return [];
    }

    // data.groups 구조
    if ('data' in response && response.data && typeof response.data === 'object') {
        if ('groups' in response.data && Array.isArray(response.data.groups)) {
            return response.data.groups;
        }
    }

    // 직접 groups 배열인 경우
    if ('groups' in response && Array.isArray(response.groups)) {
        return response.groups;
    }

    return [];
};

/**
 * codes에서 그룹 목록 추출 (그룹 정보가 없을 때 fallback)
 * @param codes 공통코드 배열
 */
export const extractGroupsFromCodes = (codes: CommonCodeItem[]): CommonCodeGroup[] => {
    const groupMap = new Map<string, CommonCodeGroup>();

    codes.forEach((code) => {
        if (!groupMap.has(code.grpCode)) {
            groupMap.set(code.grpCode, {
                grpCode: code.grpCode,
                grpName: code.grpName || code.grpCode,
                grpVal: code.grpVal || code.grpCode,
            });
        }
    });

    return Array.from(groupMap.values());
};

/**
 * 코드 정렬 (순번 기준)
 * @param codes 공통코드 배열
 */
export const sortCodes = (codes: CommonCodeItem[]): CommonCodeItem[] => {
    return [...codes].sort((a, b) => {
        const seqA = a.comSeqno || a.seqNo || 0;
        const seqB = b.comSeqno || b.seqNo || 0;
        return seqA - seqB;
    });
};

/**
 * 코드값 필드 개수 계산 (val1~val5 중 값이 있는 마지막 필드)
 * @param code 공통코드 아이템
 */
export const calculateVisibleValueFields = (code: CommonCodeItem): number => {
    if (code.val5) return 5;
    if (code.val4) return 4;
    if (code.val3) return 3;
    if (code.val2) return 2;
    return 1;
};
